import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MaterialTable from 'material-table';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { api } from 'common';
import {
  DEFAULT_ROLE_PERMISSIONS,
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
  ROLE_DEFINITIONS,
  ROLE_SCOPES,
  getRolePermissions,
  getUserRole,
  hasPermission,
  mergeUserPermissions,
  normalizePermissions,
} from '../common/permissionUtils';

const scopeOptions = (t) => [
  { value: 'all', label: t('scope_all') || 'All data' },
  { value: 'partner', label: t('scope_partner') || 'Partner data' },
  { value: 'fleet', label: t('scope_fleet') || 'Fleet data' },
  { value: 'self', label: t('scope_self') || 'Own data only' },
];

const normalizeRoleScopes = (settings) => ({
  ...ROLE_SCOPES,
  ...(settings?.roleScopes || {}),
});

const buildOverrides = (settings, roleKey, fullPermissions) => {
  const base = getRolePermissions(settings, roleKey);
  const overrides = {};
  PERMISSION_MODULES.forEach((module) => {
    const moduleKey = module.key;
    const diffs = {};
    PERMISSION_ACTIONS.forEach((action) => {
      const value = fullPermissions?.[moduleKey]?.[action];
      if (value !== base?.[moduleKey]?.[action]) {
        diffs[action] = Boolean(value);
      }
    });
    if (Object.keys(diffs).length) {
      overrides[moduleKey] = diffs;
    }
  });
  return overrides;
};

export default function AccessControl() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { editSettings, fetchUsersOnce, editUser } = api;
  const settingsdata = useSelector((state) => state.settingsdata);
  const usersdata = useSelector((state) => state.usersdata);
  const auth = useSelector((state) => state.auth);

  const [tabValue, setTabValue] = useState(0);
  const [activeRole, setActiveRole] = useState('admin');
  const [rolePermissions, setRolePermissions] = useState(DEFAULT_ROLE_PERMISSIONS);
  const [roleScopes, setRoleScopes] = useState(ROLE_SCOPES);
  const [isSaving, setIsSaving] = useState(false);

  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionUser, setPermissionUser] = useState(null);
  const [permissionState, setPermissionState] = useState(DEFAULT_ROLE_PERMISSIONS.admin);

  const settingsValue = useMemo(() => settingsdata.settings || {}, [settingsdata.settings]);
  const canManageRoles = hasPermission(auth.profile, settingsValue, 'settings', 'update');
  const canManageUsers = hasPermission(auth.profile, settingsValue, 'users', 'update');

  useEffect(() => {
    if (!usersdata.staticusers) {
      dispatch(fetchUsersOnce());
    }
  }, [dispatch, fetchUsersOnce, usersdata.staticusers]);

  useEffect(() => {
    if (settingsdata.settings) {
      const normalizedPermissions = settingsdata.settings.rolePermissions
        ? normalizePermissions(settingsdata.settings.rolePermissions)
        : DEFAULT_ROLE_PERMISSIONS;
      setRolePermissions(normalizedPermissions);
      setRoleScopes(normalizeRoleScopes(settingsdata.settings));
    }
  }, [settingsdata.settings]);

  useEffect(() => {
    if (!settingsdata.loading && isSaving) {
      setIsSaving(false);
    }
  }, [settingsdata.loading, isSaving]);

  useEffect(() => {
    if (permissionUser) {
      setPermissionState(mergeUserPermissions(settingsValue, permissionUser));
    }
  }, [permissionUser, settingsValue]);

  const roleLookup = useMemo(() => {
    return ROLE_DEFINITIONS.reduce((acc, role) => {
      acc[role.key] = role.label;
      return acc;
    }, {});
  }, []);

  const scopeLookup = useMemo(() => {
    return scopeOptions(t).reduce((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
  }, [t]);

  const partnerOptions = useMemo(() => {
    const partners = settingsValue.partnerPages || [];
    const list = Array.isArray(partners) ? partners : Object.values(partners || {});
    return list
      .map((partner) => ({
        value: partner.slug || partner.partnerSlug || partner.companySlug || '',
        label: partner.name || partner.companyName || partner.slug || '',
      }))
      .filter((item) => item.value);
  }, [settingsValue.partnerPages]);

  const fleetOptions = useMemo(() => {
    const list = usersdata.staticusers || [];
    return list
      .filter((user) => user.usertype === 'fleetadmin')
      .map((user) => ({
        value: user.id,
        label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.id,
      }))
      .filter((item) => item.value);
  }, [usersdata.staticusers]);

  const userRows = useMemo(() => {
    const list = usersdata.staticusers || [];
    return list.map((user) => {
      const resolvedRole = user.accessRole || getUserRole(user);
      const resolvedScope = user.dataScope || roleScopes[resolvedRole] || 'self';
      return {
        ...user,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        accessRole: resolvedRole,
        dataScope: resolvedScope,
        allowedPartners: Array.isArray(user.allowedPartners) ? user.allowedPartners : [],
        allowedFleets: Array.isArray(user.allowedFleets) ? user.allowedFleets : [],
        hasCustomPermissions: user.permissions && Object.keys(user.permissions).length > 0,
      };
    });
  }, [usersdata.staticusers, roleScopes]);

  const handleRolePermissionChange = (moduleKey, action, value) => {
    setRolePermissions((prev) => ({
      ...prev,
      [activeRole]: {
        ...prev[activeRole],
        [moduleKey]: {
          ...prev?.[activeRole]?.[moduleKey],
          [action]: value,
        },
      },
    }));
  };

  const handleRoleScopeChange = (value) => {
    setRoleScopes((prev) => ({
      ...prev,
      [activeRole]: value,
    }));
  };

  const handleSaveRoles = () => {
    if (!settingsdata.settings) return;
    setIsSaving(true);
    const updated = {
      ...settingsdata.settings,
      rolePermissions,
      roleScopes,
    };
    dispatch(editSettings(updated));
  };

  const handleResetRole = () => {
    setRolePermissions((prev) => ({
      ...prev,
      [activeRole]: DEFAULT_ROLE_PERMISSIONS[activeRole],
    }));
    setRoleScopes((prev) => ({
      ...prev,
      [activeRole]: ROLE_SCOPES[activeRole] || 'self',
    }));
  };

  const openPermissionDialog = (user) => {
    setPermissionUser(user);
    setPermissionDialogOpen(true);
  };

  const closePermissionDialog = () => {
    setPermissionDialogOpen(false);
    setPermissionUser(null);
  };

  const handleUserPermissionChange = (moduleKey, action, value) => {
    setPermissionState((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [action]: value,
      },
    }));
  };

  const handleResetUserPermissions = () => {
    if (!permissionUser) return;
    const roleKey = permissionUser.accessRole || getUserRole(permissionUser);
    setPermissionState(getRolePermissions(settingsValue, roleKey));
  };

  const handleSaveUserPermissions = () => {
    if (!permissionUser) return;
    const roleKey = permissionUser.accessRole || getUserRole(permissionUser);
    const overrides = buildOverrides(settingsValue, roleKey, permissionState);
    const updatedUser = {
      ...permissionUser,
      permissions: Object.keys(overrides).length ? overrides : null,
    };
    delete updatedUser.tableData;
    delete updatedUser.location;
    delete updatedUser.name;
    delete updatedUser.hasCustomPermissions;
    dispatch(editUser(permissionUser.id, updatedUser));
    closePermissionDialog();
  };

  const rolesDirty = useMemo(() => {
    const current = settingsdata.settings?.rolePermissions
      ? normalizePermissions(settingsdata.settings.rolePermissions)
      : DEFAULT_ROLE_PERMISSIONS;
    const currentScopes = normalizeRoleScopes(settingsdata.settings);
    return JSON.stringify(current) !== JSON.stringify(rolePermissions)
      || JSON.stringify(currentScopes) !== JSON.stringify(roleScopes);
  }, [rolePermissions, roleScopes, settingsdata.settings]);

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('access_control') || 'Access control'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280' }}>
          {t('access_control_subtitle') || 'Define roles, permissions, and data visibility for partners and teams.'}
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)', mb: 3 }}>
        <CardContent>
          <Tabs
            value={tabValue}
            onChange={(event, newValue) => setTabValue(newValue)}
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{ mb: 2 }}
          >
            <Tab
              icon={<TuneRoundedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={t('role_permissions') || 'Role permissions'}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<PeopleAltRoundedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={t('user_access') || 'User access'}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>

          {tabValue === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Stack spacing={1.5}>
                  {ROLE_DEFINITIONS.map((role) => (
                    <Paper
                      key={role.key}
                      elevation={0}
                      onClick={() => setActiveRole(role.key)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: activeRole === role.key ? '1px solid #111827' : '1px solid #E5E7EB',
                        cursor: 'pointer',
                        bgcolor: activeRole === role.key ? '#F9FAFB' : '#FFFFFF',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <SecurityRoundedIcon sx={{ fontSize: 18, color: '#111827' }} />
                        <Typography sx={{ fontWeight: 600 }}>{role.label}</Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>
                        {role.description}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip
                          size="small"
                          label={scopeLookup[roleScopes[role.key] || 'self']}
                          sx={{ bgcolor: '#EEF2FF', color: '#1D4ED8' }}
                        />
                        {rolePermissions[role.key] && (
                          <Chip
                            size="small"
                            label={t('permissions') || 'Permissions'}
                            sx={{ bgcolor: '#F3F4F6' }}
                          />
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12} md={8}>
                <Stack spacing={2}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {roleLookup[activeRole]}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                          {t('manage_role_permissions') || 'Toggle read, create, update, and delete access.'}
                        </Typography>
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                          value={roleScopes[activeRole] || 'self'}
                          onChange={(event) => handleRoleScopeChange(event.target.value)}
                          disabled={!canManageRoles}
                        >
                          {scopeOptions(t).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={2}>
                      {PERMISSION_MODULES.map((module) => (
                        <Box
                          key={module.key}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid #F1F5F9',
                            bgcolor: '#F8FAFC',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>{module.label}</Typography>
                              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                {module.description}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              {PERMISSION_ACTIONS.map((action) => (
                                <FormControlLabel
                                  key={action}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={Boolean(rolePermissions?.[activeRole]?.[module.key]?.[action])}
                                      onChange={(event) =>
                                        handleRolePermissionChange(module.key, action, event.target.checked)
                                      }
                                      disabled={!canManageRoles}
                                    />
                                  }
                                  label={t(`perm_${action}`) || action}
                                  sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>

                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
                      <Button
                        variant="outlined"
                        startIcon={<RestartAltRoundedIcon />}
                        onClick={handleResetRole}
                        disabled={!canManageRoles}
                      >
                        {t('reset_defaults') || 'Reset to defaults'}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SaveRoundedIcon />}
                        onClick={handleSaveRoles}
                        disabled={!canManageRoles || !rolesDirty || isSaving}
                      >
                        {isSaving ? t('saving') || 'Saving' : t('save_changes') || 'Save changes'}
                      </Button>
                    </Stack>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Box sx={{ mt: 1 }}>
              <MaterialTable
                title={t('user_access') || 'User access'}
                columns={[
                  {
                    title: t('name') || 'Name',
                    field: 'name',
                    editable: 'never',
                    render: (row) => (
                      <Stack>
                        <Typography sx={{ fontWeight: 600 }}>{row.name || row.email}</Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280' }}>{row.email}</Typography>
                      </Stack>
                    ),
                  },
                  {
                    title: t('role') || 'Role',
                    field: 'accessRole',
                    lookup: roleLookup,
                  },
                  {
                    title: t('data_scope') || 'Data scope',
                    field: 'dataScope',
                    lookup: scopeLookup,
                  },
                  {
                    title: t('allowed_partners') || 'Allowed partners',
                    field: 'allowedPartners',
                    render: (row) => (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(row.allowedPartners || []).map((slug) => (
                          <Chip key={slug} size="small" label={slug} sx={{ mb: 0.5 }} />
                        ))}
                      </Stack>
                    ),
                    editComponent: (props) => (
                      <Select
                        multiple
                        fullWidth
                        value={props.value || []}
                        onChange={(event) => props.onChange(event.target.value)}
                        renderValue={(selected) => (selected || []).join(', ')}
                      >
                        {partnerOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Checkbox checked={(props.value || []).includes(option.value)} />
                            <Typography>{option.label}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    ),
                  },
                  {
                    title: t('allowed_fleets') || 'Allowed fleets',
                    field: 'allowedFleets',
                    render: (row) => (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(row.allowedFleets || []).map((fleetId) => {
                          const label = fleetOptions.find((option) => option.value === fleetId)?.label || fleetId;
                          return <Chip key={fleetId} size="small" label={label} sx={{ mb: 0.5 }} />;
                        })}
                      </Stack>
                    ),
                    editComponent: (props) => (
                      <Select
                        multiple
                        fullWidth
                        value={props.value || []}
                        onChange={(event) => props.onChange(event.target.value)}
                        renderValue={(selected) => (selected || []).join(', ')}
                      >
                        {fleetOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Checkbox checked={(props.value || []).includes(option.value)} />
                            <Typography>{option.label}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    ),
                  },
                  {
                    title: t('custom_permissions') || 'Custom permissions',
                    field: 'hasCustomPermissions',
                    editable: 'never',
                    render: (row) => (
                      row.hasCustomPermissions ? <Chip size="small" label={t('custom') || 'Custom'} /> : '-'
                    ),
                  },
                ]}
                data={userRows}
                options={{
                  actionsColumnIndex: -1,
                  paging: true,
                  pageSize: 10,
                  search: true,
                  toolbar: true,
                  headerStyle: { fontWeight: 700 },
                }}
                localization={{
                  body: {
                    editTooltip: t('edit') || 'Edit',
                    emptyDataSourceMessage: t('no_users') || 'No users found',
                  },
                  toolbar: {
                    searchPlaceholder: t('search') || 'Search',
                  },
                }}
                actions={canManageUsers ? [
                  {
                    icon: () => <EditRoundedIcon />,
                    tooltip: t('edit_permissions') || 'Edit permissions',
                    onClick: (event, rowData) => openPermissionDialog(rowData),
                  },
                ] : []}
                editable={
                  canManageUsers
                    ? {
                        onRowUpdate: (newData, oldData) =>
                          new Promise((resolve) => {
                            const roleKey = newData.accessRole;
                            const scopeValue = newData.dataScope || roleScopes[roleKey] || 'self';
                            const updatedUser = {
                              ...oldData,
                              ...newData,
                              accessRole: roleKey,
                              dataScope: scopeValue,
                              allowedPartners: Array.isArray(newData.allowedPartners)
                                ? newData.allowedPartners
                                : [],
                              allowedFleets: Array.isArray(newData.allowedFleets) ? newData.allowedFleets : [],
                            };
                            delete updatedUser.tableData;
                            delete updatedUser.location;
                            delete updatedUser.name;
                            delete updatedUser.hasCustomPermissions;
                            dispatch(editUser(updatedUser.id, updatedUser));
                            resolve();
                          }),
                      }
                    : {}
                }
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={permissionDialogOpen}
        onClose={closePermissionDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('edit_permissions') || 'Edit permissions'}
          </Typography>
          <IconButton onClick={closePermissionDialog}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {permissionUser && (
            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{permissionUser.name || permissionUser.email}</Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {roleLookup[permissionUser.accessRole || getUserRole(permissionUser)]}
                </Typography>
              </Box>
              {PERMISSION_MODULES.map((module) => (
                <Box
                  key={module.key}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid #F1F5F9',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>{module.label}</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {PERMISSION_ACTIONS.map((action) => (
                      <FormControlLabel
                        key={action}
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(permissionState?.[module.key]?.[action])}
                            onChange={(event) =>
                              handleUserPermissionChange(module.key, action, event.target.checked)
                            }
                            disabled={!canManageUsers}
                          />
                        }
                        label={t(`perm_${action}`) || action}
                        sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={handleResetUserPermissions}>
            {t('reset_defaults') || 'Reset to defaults'}
          </Button>
          <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={handleSaveUserPermissions}>
            {t('save_changes') || 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
